import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { clearToken } from '../state/authSlice';
import { getStaffSessionToken, staffSessionHeaders } from '../staffSession';

type StaffSession = { session_hash: string; email: string; created_at: string; expires_at: string; current: boolean };
type Account = { email: string; enabled: boolean; can_manage_staff: boolean; active_sessions: number };
type SessionList = { staffEmail: string; canManageStaff: boolean; sessions: StaffSession[] };

const base = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_DEV_BASE_URL || 'http://192.168.1.85:3001/'
  : process.env.REACT_APP_PROD_BASE_URL || 'https://new-test-w4ono.ondigitalocean.app';

async function staffRequest(path: string, body?: unknown) {
  const response = await fetch(base.replace(/\/$/, '') + '/auth/' + path, {
    method: body === undefined ? 'GET' : 'POST', cache: 'no-store',
    headers: { ...staffSessionHeaders(), 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Could not complete this request.');
  return data;
}

export function StaffAccess() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState<SessionList | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function load() {
    if (!getStaffSessionToken()) { navigate('/team-login?next=/team/access'); return; }
    const result: SessionList = await staffRequest('staff-sessions');
    setData(result);
    if (result.canManageStaff) setAccounts((await staffRequest('staff-access')).accounts);
  }
  useEffect(() => { load().catch(e => setError(e.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function act(path: string, body: unknown, signsOut = false) {
    setBusy(true); setError('');
    try {
      await staffRequest(path, body);
      if (signsOut) { dispatch(clearToken()); navigate('/team-login'); return; }
      await load();
      toast.success('Staff access updated.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  const date = (value: string) => new Date(value).toLocaleString('en-GB');
  return <Page>
    <a href="/admin/select-user">← Customer accounts</a>
    <h1>Staff access &amp; sessions</h1>
    {data && <p>Signed in as <strong>{data.staffEmail}</strong>. Sessions expire after eight hours.</p>}
    <p>Revoking a session ends its access immediately, including any customer account opened through it.</p>
    {error && <p role="alert">{error}</p>}
    {data?.canManageStaff && <section>
      <h2>Staff access</h2>
      <p>Disabling access blocks new staff sign-ins and revokes every existing session. Use this when removing a team member’s Google access.</p>
      {accounts.map(account => <Row key={account.email}>
        <div><strong>{account.email}</strong><p>{account.enabled ? 'Enabled' : 'Disabled'} · {account.active_sessions} active sessions</p></div>
        {account.email !== data.staffEmail && <button disabled={busy} onClick={() => act('staff-access', { email: account.email, enabled: !account.enabled })}>
          {account.enabled ? 'Disable access' : 'Enable access'}
        </button>}
      </Row>)}
    </section>}
    <section>
      <h2>{data?.canManageStaff ? 'Active staff sessions' : 'Your active sessions'}</h2>
      {data?.sessions.map(session => <Row key={session.session_hash}>
        <div><strong>{session.email}{session.current ? ' · this session' : ''}</strong>
          <p>Started {date(session.created_at)}<br />Expires {date(session.expires_at)}</p></div>
        <button disabled={busy} onClick={() => act('staff-sessions/revoke', { session_hash: session.session_hash }, session.current)}>
          {session.current ? 'Sign out' : 'Revoke session'}
        </button>
      </Row>)}
    </section>
  </Page>;
}

const Page = styled.main`
  max-width: 850px; margin: 0 auto; padding: 24px 16px;
  section { background: white; padding: 20px; border-radius: 8px; margin: 24px 0; }
  p { line-height: 1.5; }
  [role='alert'] { color: #a32121; }
  button { padding: 10px 14px; cursor: pointer; min-height: 44px; }
`;
const Row = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 12px; padding: 14px 0; border-bottom: 1px solid #ddd;
  p { margin: 6px 0; }
  strong { overflow-wrap: anywhere; }
`;
